import { useMemo, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import {
  Activity,
  ArrowUpRight,
  Battery,
  Building2,
  Cpu,
  Factory,
  Gauge,
  HardHat,
  IndianRupee,
  Layers,
  MapPin,
  Radio,
  Settings2,
  ShieldAlert,
  Sun,
  TrendingDown,
  TrendingUp,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import PageMeta from '../../shared/PageMeta';
import SolarFieldHero from './superAdmin/illustrations/SolarFieldHero';
import OmsControlHero from './superAdmin/illustrations/OmsControlHero';
import EpcSiteConstructionHero from './superAdmin/illustrations/EpcSiteConstructionHero';

/* ============================================================
 * STATIC DUMMY DATA — Solar EPC + OMS portfolio
 * ============================================================ */

type Trend = 'up' | 'down' | 'flat';

interface KpiTile {
  label: string;
  value: string;
  unit: string;
  delta: string;
  trend: Trend;
  icon: typeof Sun;
  accent: 'solar' | 'epc' | 'oms' | 'energy' | 'people' | 'finance';
  sub: string;
}

const KPI_TILES: KpiTile[] = [
  { label: 'Portfolio Capacity', value: '4,280', unit: 'MW',  delta: '+11.4% YoY',     trend: 'up',   icon: Sun,         accent: 'solar',   sub: '2,945 MW commissioned · 985 MW under build' },
  { label: 'Active EPC Projects',value: '47',    unit: 'live',delta: '+5 this month',  trend: 'up',   icon: HardHat,     accent: 'epc',     sub: '12 nearing commissioning · 3 at risk' },
  { label: 'OMS Sites Online',   value: '132',   unit: '/ 134',delta: '98.7% uptime',  trend: 'up',   icon: Radio,       accent: 'oms',     sub: '2 sites in degraded mode' },
  { label: 'Energy Generated',   value: '5,210', unit: 'GWh', delta: '+8.2% vs target',trend: 'up',   icon: Zap,         accent: 'energy',  sub: 'CUF 22.4% · PR 81.6%' },
  { label: 'Workforce Deployed', value: '4,286', unit: 'people',delta: '+312 MoM',     trend: 'up',   icon: Users,       accent: 'people',  sub: '1,840 site · 612 OMS · 184 engineering' },
  { label: 'Capex YTD',          value: '8,472', unit: '₹ Cr', delta: '92% of plan',   trend: 'flat', icon: IndianRupee, accent: 'finance', sub: 'Burn rate: ₹706 Cr / month' },
];

const ACCENT_PALETTE: Record<KpiTile['accent'], { tint: string; bar: string; chip: string; icon: string; iconBg: string }> = {
  solar:   { tint: '#F2F8EC', bar: '#A6CE39', chip: 'text-emerald-900', icon: 'text-emerald-800', iconBg: 'bg-[#EFF7DD]' },
  epc:     { tint: '#E8F4EE', bar: '#076333', chip: 'text-emerald-900', icon: 'text-emerald-800', iconBg: 'bg-[#E2EFDB]' },
  oms:     { tint: '#E8F4EE', bar: '#52BF9D', chip: 'text-emerald-900', icon: 'text-emerald-800', iconBg: 'bg-[#DDF1E6]' },
  energy:  { tint: '#F2F8EC', bar: '#006346', chip: 'text-emerald-900', icon: 'text-emerald-800', iconBg: 'bg-[#E2EFDB]' },
  people:  { tint: '#EAF5EE', bar: '#2E9777', chip: 'text-emerald-900', icon: 'text-emerald-800', iconBg: 'bg-[#DDF1E6]' },
  finance: { tint: '#F0F6E9', bar: '#076333', chip: 'text-emerald-900', icon: 'text-emerald-800', iconBg: 'bg-[#E8F2DB]' },
};

const CRITICAL_PROJECTS = [
  { name: 'Thar Mega Park – Phase 3', code: 'THAR-3', capacity: 600, location: 'Rajasthan', progress: 78, risk: 'medium', deadline: '15 Sep 2026', spi: 0.94, cpi: 1.02 },
  { name: 'Bikaner Solar Cluster',    code: 'BIK-2',  capacity: 450, location: 'Rajasthan', progress: 62, risk: 'high',   deadline: '30 Nov 2026', spi: 0.86, cpi: 0.97 },
  { name: 'Pavagada Extension',       code: 'PVG-X',  capacity: 320, location: 'Karnataka', progress: 91, risk: 'low',    deadline: '04 Jul 2026', spi: 1.04, cpi: 1.08 },
  { name: 'Kutch Hybrid (Solar+BESS)',code: 'KUT-HY', capacity: 280, location: 'Gujarat',   progress: 41, risk: 'medium', deadline: '22 Mar 2027', spi: 0.92, cpi: 0.99 },
  { name: 'Anantapur Phase 2',        code: 'ANT-2',  capacity: 240, location: 'AP',        progress: 85, risk: 'low',    deadline: '12 Aug 2026', spi: 1.01, cpi: 1.03 },
  { name: 'Jaisalmer 750',            code: 'JSM-750',capacity: 750, location: 'Rajasthan', progress: 34, risk: 'high',   deadline: '10 Jun 2027', spi: 0.81, cpi: 0.93 },
];

const FLEET_STATUS = [
  { type: 'PV Modules',         units: 9_840_000, healthy: 99.4, icon: Sun,        accent: 'amber' },
  { type: 'Central Inverters',  units: 1_842,     healthy: 96.8, icon: Cpu,        accent: 'orange' },
  { type: 'String Inverters',   units: 14_260,    healthy: 97.2, icon: Layers,     accent: 'sky' },
  { type: 'Trackers',           units: 38_410,    healthy: 98.1, icon: Settings2,  accent: 'violet' },
  { type: 'Transformers',       units: 286,       healthy: 99.1, icon: Factory,    accent: 'rose' },
  { type: 'BESS Containers',    units: 64,        healthy: 95.6, icon: Battery,    accent: 'emerald' },
];

const FLEET_ACCENT: Record<string, { dot: string; text: string; bg: string; bar: string }> = {
  amber:   { dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50',   bar: 'from-amber-400 to-amber-500' },
  orange:  { dot: 'bg-orange-500',  text: 'text-[#006346]',  bg: 'bg-orange-50',  bar: 'from-orange-400 to-orange-500' },
  sky:     { dot: 'bg-sky-500',     text: 'text-sky-700',     bg: 'bg-sky-50',     bar: 'from-sky-400 to-sky-500' },
  violet:  { dot: 'bg-violet-500',  text: 'text-violet-700',  bg: 'bg-violet-50',  bar: 'from-violet-400 to-violet-500' },
  rose:    { dot: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50',    bar: 'from-rose-400 to-rose-500' },
  emerald: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', bar: 'from-emerald-400 to-emerald-500' },
};

const ACTIVITY_FEED = [
  { time: '2 min ago',  text: 'Auto-reset: Tracker row 14-B at Pavagada — status normal', tag: 'OMS',     accent: 'emerald' },
  { time: '14 min ago', text: 'Thar-3: MV cable laying milestone marked 76%',             tag: 'EPC',     accent: 'amber' },
  { time: '32 min ago', text: 'High temperature alarm cleared at Bikaner-2 inverter room',tag: 'Alert',   accent: 'orange' },
  { time: '1 hr ago',   text: 'Capex tranche ₹148 Cr released to Jaisalmer-750',          tag: 'Finance', accent: 'sky' },
  { time: '2 hr ago',   text: '38 string monitors auto-recovered overnight',              tag: 'AI',      accent: 'violet' },
  { time: '3 hr ago',   text: 'Kutch Hybrid: BESS commissioning plan approved',           tag: 'EPC',     accent: 'amber' },
  { time: '4 hr ago',   text: 'Zero LTI across all 132 sites for 41 days',                tag: 'Safety',  accent: 'emerald' },
];

const ACTIVITY_TAG: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  amber:   'bg-amber-100 text-amber-700 ring-amber-200',
  orange:  'bg-[#E2EFDB] text-[#006346] ring-orange-200',
  sky:     'bg-sky-100 text-sky-700 ring-sky-200',
  violet:  'bg-violet-100 text-violet-700 ring-violet-200',
};

const SITES_MAP = [
  { name: 'Thar Solar Park',     state: 'Rajasthan', mw: 1100, pr: 82.1, uptime: 99.2, alerts: 1, x: 28, y: 30 },
  { name: 'Pavagada UMSP',       state: 'Karnataka', mw: 720,  pr: 83.4, uptime: 99.6, alerts: 0, x: 40, y: 70 },
  { name: 'Bhadla Phase 4',      state: 'Rajasthan', mw: 500,  pr: 81.8, uptime: 98.7, alerts: 2, x: 30, y: 28 },
  { name: 'Kurnool Solar',       state: 'AP',        mw: 480,  pr: 80.6, uptime: 99.0, alerts: 1, x: 50, y: 70 },
  { name: 'Rewa UMSP',           state: 'MP',        mw: 405,  pr: 79.2, uptime: 97.8, alerts: 3, x: 50, y: 47 },
  { name: 'Charanka Solar Park', state: 'Gujarat',   mw: 380,  pr: 78.4, uptime: 98.4, alerts: 1, x: 24, y: 46 },
  { name: 'Kamuthi Plant',       state: 'TN',        mw: 360,  pr: 80.1, uptime: 98.9, alerts: 0, x: 48, y: 86 },
  { name: 'NP Kunta',            state: 'AP',        mw: 320,  pr: 79.7, uptime: 98.2, alerts: 2, x: 48, y: 74 },
  { name: 'Kutch Hybrid',        state: 'Gujarat',   mw: 280,  pr: 81.3, uptime: 98.5, alerts: 1, x: 18, y: 44 },
  { name: 'Jaisalmer 750',       state: 'Rajasthan', mw: 750,  pr: 80.8, uptime: 99.3, alerts: 0, x: 23, y: 32 },
];

interface FinancialItem { label: string; value: string; delta: string; trend: Trend; spark: number[]; }
const FINANCIALS: FinancialItem[] = [
  { label: 'Revenue YTD',  value: '₹5,118 Cr', delta: '+18.4%',  trend: 'up',   spark: [40, 48, 51, 55, 58, 62, 68, 72, 79, 84, 90, 96] },
  { label: 'EBITDA',       value: '₹2,261 Cr', delta: '+22.1%',  trend: 'up',   spark: [22, 24, 28, 31, 33, 36, 39, 44, 49, 53, 58, 62] },
  { label: 'Capex Burn',   value: '₹706 Cr/mo',delta: '92% plan',trend: 'flat', spark: [60, 64, 71, 68, 74, 78, 81, 84, 80, 86, 90, 88] },
  { label: 'PPA Realized', value: '₹3.18 /kWh',delta: '+₹0.12',  trend: 'up',   spark: [3.0, 3.02, 3.05, 3.04, 3.07, 3.09, 3.11, 3.12, 3.13, 3.15, 3.17, 3.18] },
];

const RISK_HEATMAP = [
  { label: 'EPC Schedule',       cells: [2, 4, 3, 1, 0] },
  { label: 'Procurement',        cells: [1, 3, 2, 0, 0] },
  { label: 'OMS Reliability',    cells: [0, 2, 1, 0, 0] },
  { label: 'Cyber / IT',         cells: [0, 1, 2, 1, 0] },
  { label: 'HSE Safety',         cells: [0, 0, 1, 0, 0] },
  { label: 'Finance / Cashflow', cells: [1, 2, 3, 1, 0] },
];

/* ============================================================
 * Style helpers (LIGHT THEME)
 * ============================================================ */

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.45 },
  }),
};

const RISK_BADGE: Record<string, string> = {
  low:    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  medium: 'bg-amber-50  text-amber-700  ring-1 ring-amber-200',
  high:   'bg-rose-50   text-rose-700   ring-1 ring-rose-200',
};

const heatmapShade = (n: number) => {
  if (n === 0) return 'bg-emerald-50  text-emerald-700 ring-1 ring-emerald-100';
  if (n === 1) return 'bg-yellow-100  text-yellow-800  ring-1 ring-yellow-200';
  if (n === 2) return 'bg-amber-200   text-amber-900   ring-1 ring-amber-300';
  if (n === 3) return 'bg-orange-300  text-orange-950  ring-1 ring-orange-400';
  return 'bg-rose-500 text-white ring-1 ring-rose-600';
};

/* ============================================================
 * Chart options — light theme tuned to flat-corporate palette
 * ============================================================ */

const energyChartOptions: ApexOptions = {
  chart: { type: 'area', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
  stroke: { curve: 'smooth', width: [3, 2, 2], dashArray: [0, 4, 4] },
  colors: ['#006346', '#5A6275', '#A6CE39'],
  fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] } },
  dataLabels: { enabled: false },
  legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px', markers: { size: 6 }, labels: { colors: '#475569' } },
  grid: { borderColor: 'rgba(148,163,184,0.18)', strokeDashArray: 4, padding: { left: 8, right: 8 } },
  xaxis: {
    categories: ['Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May','Jun'],
    labels: { style: { fontSize: '11px', colors: '#64748b' } },
    axisBorder: { show: false }, axisTicks: { show: false },
  },
  yaxis: { labels: { style: { fontSize: '11px', colors: '#64748b' }, formatter: (v) => `${v}` } },
  tooltip: { theme: 'light' },
};
const energyChartSeries = [
  { name: 'Actual',     data: [378, 412, 446, 502, 458, 410, 392, 388, 432, 481, 524, 568] },
  { name: 'Target',     data: [360, 398, 430, 478, 442, 398, 380, 376, 418, 460, 502, 540] },
  { name: 'AI Forecast',data: [365, 402, 438, 488, 450, 402, 384, 380, 422, 468, 510, 548] },
];

const ticketTrendOptions: ApexOptions = {
  chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
  stroke: { curve: 'smooth', width: 3 },
  colors: ['#C0432F', '#006346'],
  legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px', labels: { colors: '#475569' } },
  dataLabels: { enabled: false },
  grid: { borderColor: 'rgba(148,163,184,0.18)', strokeDashArray: 4 },
  xaxis: {
    categories: ['W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12'],
    labels: { style: { fontSize: '11px', colors: '#64748b' } },
    axisBorder: { show: false }, axisTicks: { show: false },
  },
  yaxis: { labels: { style: { fontSize: '11px', colors: '#64748b' } } },
  tooltip: { theme: 'light' },
  markers: { size: 4, strokeWidth: 0 },
};
const ticketTrendSeries = [
  { name: 'Corrective (CM)', data: [62, 58, 71, 55, 49, 53, 48, 44, 41, 39, 36, 32] },
  { name: 'Preventive (PM)', data: [88, 94, 102, 110, 118, 124, 132, 138, 142, 148, 152, 158] },
];

const fleetDonutOptions: ApexOptions = {
  chart: { type: 'donut', fontFamily: 'inherit', background: 'transparent' },
  labels: ['Healthy', 'Watch', 'Degraded', 'Faulted'],
  colors: ['#006346', '#A6CE39', '#F08A3E', '#C0432F'],
  legend: { position: 'bottom', fontSize: '11px', labels: { colors: '#475569' } },
  stroke: { width: 2, colors: ['#FBFEF9'] },
  dataLabels: { enabled: false },
  plotOptions: {
    pie: {
      donut: {
        size: '72%',
        labels: {
          show: true,
          name: { fontSize: '11px', color: '#64748b' },
          value: { fontSize: '22px', fontWeight: 700, color: '#1f2937' },
          total: { show: true, label: 'Fleet Health', formatter: () => '97.5%', color: '#64748b' },
        },
      },
    },
  },
  tooltip: { theme: 'light' },
};
const fleetDonutSeries = [97.5, 1.4, 0.8, 0.3];

const workforceRadialOptions: ApexOptions = {
  chart: { type: 'radialBar', fontFamily: 'inherit', background: 'transparent' },
  colors: ['#006346', '#52BF9D', '#A6CE39'],
  labels: ['EPC', 'OMS', 'Engg'],
  plotOptions: {
    radialBar: {
      hollow: { size: '32%' },
      track: { background: 'rgba(148,163,184,0.18)' },
      dataLabels: {
        name: { fontSize: '11px', color: '#64748b' },
        value: { fontSize: '14px', fontWeight: 700, color: '#1f2937' },
        total: { show: true, label: 'Utilization', formatter: () => '87%', color: '#64748b' },
      },
    },
  },
  legend: { show: true, position: 'bottom', fontSize: '11px', labels: { colors: '#475569' } },
  stroke: { lineCap: 'round' },
};
const workforceRadialSeries = [92, 84, 78];

const milestoneRadialOptions: ApexOptions = {
  chart: { type: 'radialBar', fontFamily: 'inherit', background: 'transparent' },
  colors: ['#006346', '#52BF9D', '#A6CE39', '#C0432F', '#076333', '#2E9777'],
  labels: ['THAR-3','BIK-2','PVG-X','JSM-750','KUT-HY','ANT-2'],
  plotOptions: {
    radialBar: {
      hollow: { size: '24%' },
      track: { background: 'rgba(148,163,184,0.15)' },
      dataLabels: {
        name: { fontSize: '10px', color: '#64748b' },
        value: { fontSize: '12px', fontWeight: 700, color: '#1f2937' },
        total: { show: true, label: 'Avg Progress', formatter: () => '65%', color: '#64748b' },
      },
    },
  },
  legend: { show: true, position: 'bottom', fontSize: '10px', labels: { colors: '#475569' } },
  stroke: { lineCap: 'round' },
};
const milestoneRadialSeries = [78, 62, 91, 34, 41, 85];

const sparkOptions = (color: string): ApexOptions => ({
  chart: { type: 'area', sparkline: { enabled: true }, background: 'transparent' },
  stroke: { curve: 'smooth', width: 2 },
  colors: [color],
  fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.02 } },
  tooltip: { enabled: false },
});

/* ============================================================
 * Card shell — light, warm, professional
 * ============================================================ */

function Card({
  children,
  className = '',
  delay = 0,
  padded = true,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  padded?: boolean;
}) {
  return (
    <motion.div
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={delay}
      className={`relative rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] ${padded ? 'p-5' : ''} ${className}`}
    >
      {children}
    </motion.div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  desc,
  action,
}: {
  eyebrow?: string;
  title: string;
  desc?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
      <div>
        {eyebrow && (
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#076333]">
            {eyebrow}
          </p>
        )}
        <h3 className="mt-0.5 text-base font-bold text-slate-900">{title}</h3>
        {desc && <p className="mt-0.5 text-xs text-slate-500">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

/* ============================================================
 * India SVG map — warm light theme
 * ============================================================ */

const INDIA_PATH =
  'M285 95 L350 110 L420 130 L480 160 L520 200 L530 250 L520 300 L510 360 L520 420 L540 480 L560 540 L580 600 L590 660 L580 720 L560 780 L540 830 L510 870 L480 900 L450 920 L420 935 L390 940 L360 935 L330 920 L300 900 L280 870 L260 830 L250 780 L240 720 L235 660 L240 600 L250 540 L240 480 L230 420 L220 360 L210 300 L215 250 L230 200 L255 160 L275 130 Z';

function IndiaSitesMap({ sites }: { sites: typeof SITES_MAP }) {
  return (
    <div className="relative h-[420px] w-full overflow-hidden rounded-xl bg-gradient-to-br from-[#FBFEF9] via-white to-[#F2F8EC]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,99,70,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(0,99,70,0.07) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at center, black 45%, transparent 90%)',
        }}
      />
      <svg viewBox="0 0 800 1000" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id="india-fill-light" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#A6CE39" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#52BF9D" stopOpacity="0.12" />
          </linearGradient>
          <filter id="map-soft-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d={INDIA_PATH} fill="url(#india-fill-light)" stroke="#006346" strokeWidth="2" strokeOpacity="0.55" />
        {sites.map((s, i) => {
          const cx = (s.x / 100) * 800;
          const cy = (s.y / 100) * 1000;
          const color = s.alerts >= 2 ? '#C0432F' : s.alerts === 1 ? '#A6CE39' : '#006346';
          return (
            <g key={s.name} filter="url(#map-soft-glow)">
              <motion.circle
                cx={cx} cy={cy} r="6" fill={color}
                initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }}
              />
              <motion.circle
                cx={cx} cy={cy} r="10" fill="none" stroke={color} strokeWidth="2"
                animate={{ r: [10, 22], opacity: [0.85, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.18 }}
              />
              <text x={cx + 14} y={cy + 4} fill="#1f2937" fontSize="14" fontFamily="system-ui" fontWeight="700" style={{ pointerEvents: 'none' }}>
                {s.mw} MW
              </text>
              <text x={cx + 14} y={cy + 22} fill="#64748b" fontSize="11" fontFamily="system-ui" style={{ pointerEvents: 'none' }}>
                {s.name}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 shadow ring-1 ring-[#A6CE39]/40">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#A6CE39]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#006346]">Live · {sites.length} Sites</span>
      </div>

      <div className="absolute left-3 top-3 flex flex-col gap-1 rounded-lg bg-white/95 p-2 shadow ring-1 ring-slate-200">
        <div className="flex items-center gap-2 text-[10px] text-slate-600">
          <span className="h-2 w-2 rounded-full bg-[#006346]" /> Optimal
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-600">
          <span className="h-2 w-2 rounded-full bg-[#A6CE39]" /> Watch
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-600">
          <span className="h-2 w-2 rounded-full bg-rose-600" /> Critical
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Main
 * ============================================================ */

export default function TempSuperAdminDashboard() {
  const [activeFilter, setActiveFilter] = useState<'all'|'critical'|'watch'|'healthy'>('all');

  const filteredSites = useMemo(() => {
    if (activeFilter === 'all') return SITES_MAP;
    if (activeFilter === 'critical') return SITES_MAP.filter(s => s.alerts >= 2);
    if (activeFilter === 'watch')    return SITES_MAP.filter(s => s.alerts === 1);
    return SITES_MAP.filter(s => s.alerts === 0);
  }, [activeFilter]);

  const nowStr = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <>
      <PageMeta title="Command Center · SuperAdmin" description="Solar EPC + OMS portfolio command center" />

      {/* full-bleed warm canvas */}
      <div className="relative -mx-4 -mt-4 px-4 pt-4 pb-12 lg:-mx-6 lg:px-6">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            backgroundColor: '#FBFEF9',
            backgroundImage:
              'radial-gradient(ellipse 80% 50% at 10% 0%, rgba(166,206,57,0.16), transparent 60%), ' +
              'radial-gradient(ellipse 70% 50% at 90% 10%, rgba(82,191,157,0.14), transparent 60%), ' +
              'radial-gradient(ellipse 60% 40% at 50% 100%, rgba(0,99,70,0.08), transparent 60%)',
          }}
        />

        <div className="relative flex flex-col gap-5">

          {/* ============ HERO BAND with SOLAR ILLUSTRATION ============ */}
          <motion.section
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="relative overflow-hidden rounded-2xl border border-[#52BF9D]/40 bg-gradient-to-r from-[#F2F8EC] via-[#FBFEF9] to-[#EAF5EE] shadow-sm"
          >
            <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
              <div className="px-6 py-6 lg:px-8 lg:py-7">
                <p className="inline-flex items-center gap-1.5 rounded-full bg-[#E2EFDB] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#006346]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  Live · Portfolio Command Center
                </p>
                <h1 className="mt-2 text-2xl font-bold leading-tight text-slate-900 lg:text-3xl">
                  Renewable Portfolio Operations
                </h1>
                <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-600">
                  Unified view of EPC delivery and OMS operations across <strong>4,280 MW</strong> of
                  installed and contracted capacity, with <strong>132 sites</strong> under live monitoring.
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { lab: 'Commissioned',  val: '2,945 MW' },
                    { lab: 'Under Build',   val: '985 MW' },
                    { lab: 'Energy YTD',    val: '5,210 GWh' },
                    { lab: 'CO₂ Avoided',   val: '4.16 M t' },
                  ].map(m => (
                    <div key={m.lab} className="rounded-lg bg-white/70 px-3 py-2 ring-1 ring-[#A6CE39]/40">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{m.lab}</p>
                      <p className="mt-0.5 text-base font-bold text-slate-900">{m.val}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                  Last sync · {nowStr}
                </p>
              </div>
              <div className="relative flex items-stretch justify-end pl-2 pr-3 lg:pl-0">
                <div className="flex w-full items-center">
                  <SolarFieldHero className="w-full" />
                </div>
              </div>
            </div>
          </motion.section>

          {/* ============ KPI TILES ============ */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {KPI_TILES.map((tile, i) => {
              const Icon = tile.icon;
              const palette = ACCENT_PALETTE[tile.accent];
              return (
                <motion.div
                  key={tile.label}
                  custom={i}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ y: -3 }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition"
                >
                  <div
                    className="absolute inset-x-0 top-0 h-[3px]"
                    style={{ backgroundColor: palette.bar }}
                  />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                        {tile.label}
                      </p>
                      <div className="mt-1 flex items-baseline gap-1.5">
                        <span className="text-2xl font-bold tracking-tight text-slate-900">{tile.value}</span>
                        <span className="text-xs font-medium text-slate-500">{tile.unit}</span>
                      </div>
                    </div>
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${palette.iconBg}`}>
                      <Icon className={`h-5 w-5 ${palette.icon}`} />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <p className="truncate text-[11px] text-slate-500">{tile.sub}</p>
                    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      tile.trend === 'up' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' :
                      tile.trend === 'down' ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' :
                      'bg-slate-50 text-slate-600 ring-1 ring-slate-200'
                    }`}>
                      {tile.trend === 'up' ? <ArrowUpRight className="h-2.5 w-2.5" />
                        : tile.trend === 'down' ? <TrendingDown className="h-2.5 w-2.5" />
                        : <Activity className="h-2.5 w-2.5" />}
                      {tile.delta}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </section>

          {/* ============ CHARTS ROW 1 ============ */}
          <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2" delay={0}>
              <SectionHeader
                eyebrow="Generation Performance"
                title="Energy Output · Actual vs Target vs AI Forecast"
                desc="GWh per month · trailing 12 months across 132 live sites"
                action={
                  <div className="flex gap-1">
                    {['1M','3M','12M','YTD','ALL'].map((t, idx) => (
                      <button key={t} className={`rounded-md px-2.5 py-1 text-[10px] font-bold transition ${
                        idx === 2
                          ? 'bg-[#006346] text-white shadow-sm'
                          : 'bg-slate-50 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
                      }`}>
                        {t}
                      </button>
                    ))}
                  </div>
                }
              />
              <Chart options={energyChartOptions} series={energyChartSeries} type="area" height={310} />
            </Card>

            <Card delay={1}>
              <SectionHeader
                eyebrow="EPC Delivery"
                title="Milestone Progress"
                desc="Top 6 active projects · % complete"
              />
              <Chart options={milestoneRadialOptions} series={milestoneRadialSeries} type="radialBar" height={310} />
            </Card>
          </section>

          {/* ============ EPC HERO BAND ============ */}
          <Card className="overflow-hidden" padded={false}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr]">
              <div className="relative p-6 lg:p-7">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E2EFDB] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#006346]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#006346]" />
                  Engineering · Procurement · Construction
                </span>
                <h3 className="mt-2 text-xl font-bold leading-tight text-slate-900 lg:text-2xl">
                  Project Health &amp; Risk Outlook
                </h3>
                <p className="mt-1 max-w-md text-sm leading-relaxed text-slate-600">
                  Six projects under critical watch, contributing <strong>2,640 MW</strong>. Schedule
                  performance trending at <strong>0.93 SPI</strong> with cost performance steady at <strong>1.00 CPI</strong>.
                </p>

                {/* SPI / CPI performance gauges */}
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {[
                    { lab: 'Schedule (SPI)', val: '0.93', tone: 'text-amber-700', dot: 'bg-amber-500' },
                    { lab: 'Cost (CPI)',     val: '1.00', tone: 'text-emerald-700', dot: 'bg-emerald-500' },
                    { lab: 'Earned Value',   val: '₹4.1 Bn', tone: 'text-[#076333]', dot: 'bg-[#52BF9D]' },
                  ].map(g => (
                    <div key={g.lab} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-2.5 py-1.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${g.dot}`} />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{g.lab}</span>
                      <span className={`text-sm font-bold ${g.tone}`}>{g.val}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-3 grid grid-cols-3 gap-3">
                  {[
                    { lab: 'On-Track',  val: '32',  pct: 'text-emerald-700', bar: 'bg-emerald-500', w: 'w-[68%]' },
                    { lab: 'Watch',     val: '12',  pct: 'text-amber-700',   bar: 'bg-amber-500',   w: 'w-[25%]' },
                    { lab: 'At Risk',   val: '3',   pct: 'text-rose-700',    bar: 'bg-rose-500',    w: 'w-[7%]' },
                  ].map(s => (
                    <div key={s.lab} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 transition hover:border-[#52BF9D]/60 hover:shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{s.lab}</p>
                      <p className={`text-2xl font-bold ${s.pct}`}>{s.val}</p>
                      <div className="mt-1 h-1 rounded-full bg-slate-100">
                        <div className={`h-full rounded-full ${s.bar} ${s.w}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative flex items-center justify-end overflow-hidden bg-gradient-to-br from-[#FBFEF9] to-[#F2F8EC]">
                <EpcSiteConstructionHero className="w-full" />
              </div>
            </div>
          </Card>

          {/* ============ CRITICAL PROJECTS + RISK ============ */}
          <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2 overflow-hidden" padded={false}>
              <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#076333]">Portfolio</p>
                  <h3 className="text-base font-bold text-slate-900 inline-flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[#076333]" />
                    Top Projects · Health & Risk
                  </h3>
                </div>
                <button className="text-[11px] font-semibold text-[#006346] hover:text-[#076333]">View portfolio →</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50/70 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <tr>
                      <th className="px-5 py-2.5 font-semibold">Project</th>
                      <th className="px-3 py-2.5 font-semibold">Capacity</th>
                      <th className="px-3 py-2.5 font-semibold">Progress</th>
                      <th className="px-3 py-2.5 font-semibold">SPI</th>
                      <th className="px-3 py-2.5 font-semibold">CPI</th>
                      <th className="px-3 py-2.5 font-semibold">Risk</th>
                      <th className="px-3 py-2.5 font-semibold">Deadline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CRITICAL_PROJECTS.map((p, i) => (
                      <motion.tr
                        key={p.code}
                        custom={i}
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                        className="border-t border-slate-200/70 transition hover:bg-[#F2F8EC]/60"
                      >
                        <td className="px-5 py-3">
                          <p className="font-semibold text-slate-900">{p.name}</p>
                          <p className="font-mono text-[10px] text-slate-500">
                            {p.code} · <MapPin className="inline h-2.5 w-2.5" /> {p.location}
                          </p>
                        </td>
                        <td className="px-3 py-3 font-mono font-semibold text-slate-800">
                          {p.capacity}<span className="ml-1 text-[10px] text-slate-500">MW</span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="relative h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${p.progress}%` }}
                                transition={{ duration: 0.8, delay: i * 0.05 }}
                                className={`h-full rounded-full ${
                                  p.progress >= 80 ? 'bg-emerald-500' :
                                  p.progress >= 50 ? 'bg-amber-500' :
                                  'bg-rose-500'
                                }`}
                              />
                            </div>
                            <span className="font-mono font-bold text-slate-700">{p.progress}%</span>
                          </div>
                        </td>
                        <td className={`px-3 py-3 font-mono font-bold ${p.spi >= 1 ? 'text-emerald-700' : p.spi >= 0.9 ? 'text-amber-700' : 'text-rose-700'}`}>
                          {p.spi.toFixed(2)}
                        </td>
                        <td className={`px-3 py-3 font-mono font-bold ${p.cpi >= 1 ? 'text-emerald-700' : p.cpi >= 0.95 ? 'text-amber-700' : 'text-rose-700'}`}>
                          {p.cpi.toFixed(2)}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${RISK_BADGE[p.risk]}`}>
                            {p.risk}
                          </span>
                        </td>
                        <td className="px-3 py-3 font-mono text-slate-600">{p.deadline}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card delay={1}>
              <SectionHeader
                eyebrow="Enterprise Risk"
                title="Likelihood × Impact"
                desc="Risks by category — weekly review"
              />
              <div className="space-y-1.5">
                <div className="grid grid-cols-[120px_repeat(5,1fr)] items-center gap-1 px-1 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                  <span />
                  <span className="text-center">Rare</span>
                  <span className="text-center">Unlikely</span>
                  <span className="text-center">Possible</span>
                  <span className="text-center">Likely</span>
                  <span className="text-center">Certain</span>
                </div>
                {RISK_HEATMAP.map((row, ri) => (
                  <div key={row.label} className="grid grid-cols-[120px_repeat(5,1fr)] items-center gap-1">
                    <span className="truncate text-[11px] font-semibold text-slate-700">{row.label}</span>
                    {row.cells.map((c, j) => (
                      <motion.div
                        key={j}
                        custom={ri * 5 + j}
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                        className={`flex h-8 items-center justify-center rounded-md text-[11px] font-bold ${heatmapShade(c)}`}
                      >
                        {c > 0 ? c : ''}
                      </motion.div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-[9px] text-slate-500">
                <span>Severity</span>
                <span className="h-2.5 w-3.5 rounded bg-emerald-50 ring-1 ring-emerald-100" />
                <span className="h-2.5 w-3.5 rounded bg-yellow-100 ring-1 ring-yellow-200" />
                <span className="h-2.5 w-3.5 rounded bg-amber-200 ring-1 ring-amber-300" />
                <span className="h-2.5 w-3.5 rounded bg-orange-300 ring-1 ring-orange-400" />
                <span className="h-2.5 w-3.5 rounded bg-rose-500" />
                <span>Low → High</span>
              </div>
            </Card>
          </section>

          {/* ============ OMS HERO BAND ============ */}
          <Card className="overflow-hidden" padded={false}>
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
              <div className="relative flex items-center justify-start overflow-hidden bg-gradient-to-br from-[#F2F8EC] to-[#FBFEF9]">
                <OmsControlHero className="w-full" />
              </div>
              <div className="p-6 lg:p-7">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#076333]">
                  Operations · Maintenance · Service
                </p>
                <h3 className="mt-1 text-xl font-bold text-slate-900">
                  Live OMS Control & Asset Performance
                </h3>
                <p className="mt-1 max-w-md text-sm text-slate-600">
                  24×7 monitoring across all live sites with predictive
                  maintenance, anomaly auto-resolution, and shift-handover analytics.
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    { lab: 'Fleet Uptime',  val: '98.7%', icon: Gauge, accent: 'emerald' },
                    { lab: 'Open Tickets',  val: '326',   icon: Wrench, accent: 'amber' },
                    { lab: 'MTTR (avg)',    val: '4h 12m',icon: Activity, accent: 'sky' },
                  ].map(s => {
                    const Ic = s.icon;
                    return (
                      <div key={s.lab} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <Ic className={`h-3 w-3 ${
                            s.accent === 'emerald' ? 'text-emerald-600' :
                            s.accent === 'amber'   ? 'text-amber-600'   :
                            'text-sky-600'
                          }`} />
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{s.lab}</p>
                        </div>
                        <p className="mt-0.5 text-xl font-bold text-slate-900">{s.val}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>

          {/* ============ CHARTS ROW 2 ============ */}
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card>
              <SectionHeader
                eyebrow="OMS Workload"
                title="Tickets · 12-week Trend"
                desc="Corrective vs Preventive maintenance"
              />
              <Chart options={ticketTrendOptions} series={ticketTrendSeries} type="line" height={250} />
            </Card>

            <Card delay={1}>
              <SectionHeader
                eyebrow="Fleet Status"
                title="Equipment Health Index"
                desc="Aggregated across all assets"
              />
              <Chart options={fleetDonutOptions} series={fleetDonutSeries} type="donut" height={250} />
            </Card>

            <Card delay={2}>
              <SectionHeader
                eyebrow="People"
                title="Workforce Utilization"
                desc="4,286 deployed across functions"
              />
              <Chart options={workforceRadialOptions} series={workforceRadialSeries} type="radialBar" height={250} />
            </Card>
          </section>

          {/* ============ MAP + ACTIVITY ============ */}
          <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <SectionHeader
                eyebrow="Geographic Distribution"
                title="Live Site Network · India"
                desc="Real-time pulse of all OMS sites"
                action={
                  <div className="flex gap-1">
                    {[
                      { k: 'all',      label: 'All' },
                      { k: 'healthy',  label: 'Optimal' },
                      { k: 'watch',    label: 'Watch' },
                      { k: 'critical', label: 'Critical' },
                    ].map(f => (
                      <button
                        key={f.k}
                        onClick={() => setActiveFilter(f.k as typeof activeFilter)}
                        className={`rounded-md px-2.5 py-1 text-[10px] font-bold transition ${
                          activeFilter === f.k
                            ? 'bg-[#006346] text-white shadow-sm'
                            : 'bg-slate-50 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                }
              />
              <IndiaSitesMap sites={filteredSites} />
            </Card>

            <Card padded={false} delay={1}>
              <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#076333]">Activity</p>
                  <h3 className="text-base font-bold text-slate-900 inline-flex items-center gap-2">
                    <Activity className="h-4 w-4 text-[#076333]" /> Live Operations Stream
                  </h3>
                </div>
                <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  Live
                </span>
              </div>
              <ul className="divide-y divide-slate-200/70">
                {ACTIVITY_FEED.map((e, i) => (
                  <motion.li
                    key={i}
                    custom={i}
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    className="flex items-start gap-3 px-5 py-3"
                  >
                    <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase ring-1 ${ACTIVITY_TAG[e.accent]}`}>
                      {e.tag}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs leading-snug text-slate-800">{e.text}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-slate-500">{e.time}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </Card>
          </section>

          {/* ============ FLEET SNAPSHOT ============ */}
          <section>
            <header className="mb-3 flex items-end justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#076333]">Asset Inventory</p>
                <h2 className="text-base font-bold text-slate-900 inline-flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-[#076333]" /> Equipment Fleet · Live Snapshot
                </h2>
              </div>
              <span className="text-[11px] text-slate-500">{FLEET_STATUS.length} categories monitored</span>
            </header>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              {FLEET_STATUS.map((eq, i) => {
                const Icon = eq.icon;
                const a = FLEET_ACCENT[eq.accent];
                return (
                  <motion.div
                    key={eq.type}
                    custom={i}
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ y: -3 }}
                    className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm transition"
                  >
                    <div className="flex items-center justify-between">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${a.bg}`}>
                        <Icon className={`h-4 w-4 ${a.text}`} />
                      </div>
                      <span className={`font-mono text-[11px] font-bold ${
                        eq.healthy >= 98 ? 'text-emerald-700' :
                        eq.healthy >= 96 ? 'text-amber-700' :
                        'text-rose-700'
                      }`}>
                        {eq.healthy}%
                      </span>
                    </div>
                    <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {eq.type}
                    </p>
                    <p className="font-mono text-lg font-bold text-slate-900">
                      {eq.units.toLocaleString()}
                    </p>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${eq.healthy}%` }}
                        transition={{ duration: 0.9, delay: i * 0.08 }}
                        className={`h-full rounded-full bg-gradient-to-r ${a.bar}`}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* ============ FINANCIAL PULSE ============ */}
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#076333]">Financial Pulse</p>
                <h3 className="text-base font-bold text-slate-900">Revenue · EBITDA · Capex · PPA</h3>
              </div>
              <button className="text-[11px] font-semibold text-[#006346] hover:text-[#076333]">Reports →</button>
            </div>
            <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
              {FINANCIALS.map((f, i) => {
                const color = f.trend === 'up' ? '#006346' : f.trend === 'down' ? '#C0432F' : '#A6CE39';
                return (
                  <motion.div
                    key={f.label}
                    custom={i}
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    className="rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-[#F2F8EC] p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{f.label}</p>
                        <p className="mt-1 font-mono text-xl font-bold text-slate-900">{f.value}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ring-1 ${
                        f.trend === 'up' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' :
                        f.trend === 'down' ? 'bg-rose-50 text-rose-700 ring-rose-200' :
                        'bg-amber-50 text-amber-700 ring-amber-200'
                      }`}>
                        {f.trend === 'up' ? <TrendingUp className="h-2.5 w-2.5" /> :
                         f.trend === 'down' ? <TrendingDown className="h-2.5 w-2.5" /> :
                         <Activity className="h-2.5 w-2.5" />}
                        {f.delta}
                      </span>
                    </div>
                    <div className="mt-1">
                      <Chart options={sparkOptions(color)} series={[{ data: f.spark }]} type="area" height={56} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Card>

          {/* ============ FOOTER ============ */}
          <motion.section
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#52BF9D]/40 bg-gradient-to-r from-[#F2F8EC] via-[#FBFEF9] to-[#EAF5EE] px-5 py-4 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E2EFDB] text-[#006346]">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#006346]">
                  Sorigin Intelligence Mesh
                </p>
                <p className="text-xs text-slate-600">
                  12 predictive models · 6 anomaly detectors · 4 weather-coupled forecasters
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              {[
                { m: 'Yield-Predictor v3' },
                { m: 'Fault-Forecaster' },
                { m: 'Schedule-Coach' },
                { m: 'Energy-Optimizer' },
                { m: 'Risk-Sense' },
              ].map(x => (
                <span key={x.m} className="rounded-full bg-white/80 px-2.5 py-1 font-semibold text-slate-700 ring-1 ring-[#A6CE39]/45">
                  {x.m}
                </span>
              ))}
            </div>
          </motion.section>

        </div>
      </div>
    </>
  );
}
